import { useEffect } from "react";

/**
 * useSeo — tiny SEO hook that swaps <title>, <meta description>, canonical,
 * OG and Twitter tags for the current route. Restores the previous values
 * on unmount so navigating back to the homepage doesn't leak the last
 * page's SEO into the tab title.
 *
 * We deliberately mutate <head> instead of importing react-helmet so we
 * add zero dependencies for something this small.
 */
export default function useSeo({ title, description, canonical, image, jsonLd }) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMeta("name", "description"),
      canonical: getLink("canonical"),
      ogTitle: getMeta("property", "og:title"),
      ogDescription: getMeta("property", "og:description"),
      ogUrl: getMeta("property", "og:url"),
      ogImage: getMeta("property", "og:image"),
      twTitle: getMeta("name", "twitter:title"),
      twDescription: getMeta("name", "twitter:description"),
      twImage: getMeta("name", "twitter:image"),
    };

    if (title) document.title = title;
    if (description) setMeta("name", "description", description);
    if (canonical) setLink("canonical", canonical);
    if (title) setMeta("property", "og:title", title);
    if (description) setMeta("property", "og:description", description);
    if (canonical) setMeta("property", "og:url", canonical);
    if (image) setMeta("property", "og:image", image);
    if (title) setMeta("name", "twitter:title", title);
    if (description) setMeta("name", "twitter:description", description);
    if (image) setMeta("name", "twitter:image", image);

    let jsonLdNode = null;
    if (jsonLd) {
      jsonLdNode = document.createElement("script");
      jsonLdNode.type = "application/ld+json";
      jsonLdNode.dataset.routeJsonld = "1";
      jsonLdNode.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdNode);
    }

    return () => {
      document.title = prev.title;
      if (prev.description !== null) setMeta("name", "description", prev.description);
      if (prev.canonical !== null) setLink("canonical", prev.canonical);
      if (prev.ogTitle !== null) setMeta("property", "og:title", prev.ogTitle);
      if (prev.ogDescription !== null) setMeta("property", "og:description", prev.ogDescription);
      if (prev.ogUrl !== null) setMeta("property", "og:url", prev.ogUrl);
      if (prev.ogImage !== null) setMeta("property", "og:image", prev.ogImage);
      if (prev.twTitle !== null) setMeta("name", "twitter:title", prev.twTitle);
      if (prev.twDescription !== null) setMeta("name", "twitter:description", prev.twDescription);
      if (prev.twImage !== null) setMeta("name", "twitter:image", prev.twImage);
      if (jsonLdNode) jsonLdNode.remove();
    };
  }, [title, description, canonical, image, jsonLd]);
}

function getMeta(attr, key) {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  return el ? el.getAttribute("content") : null;
}
function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}
function getLink(rel) {
  const el = document.head.querySelector(`link[rel="${rel}"]`);
  return el ? el.getAttribute("href") : null;
}
function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
